// Feedback after a check: praise and the author's explanation when right; the specific reason first, then the
// explanation and any variant note when wrong. Rendered inside the player's aria-live region.
// Kids: a right answer pops a star with a small decorative confetti burst (aria-hidden, off under reduced motion).

import type { FC, ReactNode } from 'react'
import type { L } from '@/prakrit/lib/lessons/types'
import type { ClientStep } from '@/prakrit/lib/lessons/resolve'
import type { GradeResponse } from '@/prakrit/components/lesson/lesson-types'

import { CircleCheck, CircleX, Star, WifiOff } from 'lucide-react'
import { Bi } from '@/prakrit/components/common/bi'
import { KidsBurst } from '@/components/kids/kids-burst'

const PRAISE_ADULTS: L[] = [
    { hi: 'सही!', en: 'Correct!' },
    { hi: 'बिल्कुल सही', en: 'Exactly right' },
    { hi: 'बहुत अच्छे', en: 'Well done' }
]

const PRAISE_KIDS: L[] = [
    { hi: 'वाह! बहुत अच्छे!', en: 'Wow, great job!' },
    { hi: 'शाबाश!', en: 'Well done!' },
    { hi: 'एकदम सही!', en: 'Spot on!' }
]

interface FeedbackPanelProps {
    result: GradeResponse | null
    error: boolean
    stepType: ClientStep['type']
    /** Picks the praise line (deterministic, no randomness in render). */
    index: number
    kids: boolean
    isTest: boolean
}

export const FeedbackPanel: FC<FeedbackPanelProps> = ({ result, error, stepType, index, kids, isTest }): ReactNode => {
    if (error) {
        return (
            <div className='flex items-start gap-3'>
                <WifiOff className='mt-0.5 size-6 shrink-0 text-danger' aria-hidden='true' />
                <p className='text-sm sm:text-base'>
                    <Bi
                        hi='उत्तर जाँचा नहीं जा सका। इंटरनेट जाँचकर फिर से कोशिश करें — आपका उत्तर यहीं है।'
                        en='We could not check your answer. Check your connection and try again; your answer is still here.'
                        hiClassName='font-medium'
                    />
                </p>
            </div>
        )
    }

    if (!result) return null

    if (result.correct) {
        const praise = (kids ? PRAISE_KIDS : PRAISE_ADULTS)[index % 3]

        return (
            <div className='animate-pop-in flex items-start gap-3'>
                {kids ? (
                    <span className='relative mt-0.5 flex shrink-0'>
                        <Star className='kid-star-pop size-6 fill-saffron text-saffron-strong' aria-hidden='true' />
                        <KidsBurst />
                    </span>
                ) : (
                    <CircleCheck className='mt-0.5 size-6 shrink-0 text-success' aria-hidden='true' />
                )}
                <div className='flex min-w-0 flex-col gap-1'>
                    <p className='text-lg font-semibold text-foreground'>
                        <Bi hi={praise.hi} en={praise.en} layout='inline' always />
                    </p>
                    <p className='text-sm sm:text-base'>
                        <Bi hi={result.feedback.hi} en={result.feedback.en} />
                    </p>
                    {result.note && (
                        <p className='text-sm text-muted-foreground'>
                            <Bi hi={result.note.hi} en={result.note.en} />
                        </p>
                    )}
                    {stepType === 'build' && (
                        <p className='text-sm font-medium text-foreground'>
                            <Bi hi='क्रम कोई भी हो सकता है' en='Any word order works' layout='inline' />
                        </p>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className='flex items-start gap-3'>
            <CircleX className='mt-0.5 size-6 shrink-0 text-danger' aria-hidden='true' />
            <div className='flex min-w-0 flex-col gap-1'>
                <p className='text-lg font-semibold text-danger'>
                    {isTest
                        ? <Bi hi='यह सही नहीं है' en='Not correct' layout='inline' always />
                        : <Bi hi='अभी नहीं — फिर से कोशिश कीजिए' en='Not quite: try again' layout='inline' always />}
                </p>
                {result.reason && (
                    <p className='text-sm font-medium sm:text-base'>
                        <Bi hi={result.reason.hi} en={result.reason.en} />
                    </p>
                )}
                <p className='text-sm sm:text-base'>
                    <Bi hi={result.feedback.hi} en={result.feedback.en} />
                </p>
                {result.note && (
                    <p className='text-sm text-muted-foreground'>
                        <Bi hi={result.note.hi} en={result.note.en} />
                    </p>
                )}
            </div>
        </div>
    )
}
